import { extractApiErrorCode } from '@hub/lib/api-error/extract-api-error-code';
import { ApiError, unsubscribeCommunicationPreferences, zUnsubscribeBodyWritable } from '@platform/api';
import { track } from '@platform/tracking';
import type { UnsubscribeErrorCode } from './errorCodes';
import { NotificationPaths } from './route.paths';

export type UnsubscribeResult = { status: 'unsubscribed' | 'invalid-link' };

/**
 * `campaign` arrives as a free-form query parameter, so it is checked against the spec rather than
 * trusted. Derived from the generated schema to stay in step with the categories the BFF accepts.
 */
const categorySchema = zUnsubscribeBodyWritable.shape.category;

/**
 * Expected outcomes of a one-click email link (params stripped by the mail client, token used
 * or expired) come back as data, not throws: every error-boundary hit is reported to Sentry
 * (`app/bootstrap/errorHandlers.ts`), which turned normal traffic into error noise.
 */
export async function loadUnsubscribe(token: string | undefined, campaign: string | undefined): Promise<UnsubscribeResult> {
  const meta = { page: NotificationPaths.unsubscribe.path };
  const category = categorySchema.safeParse(campaign);

  // A missing or unrecognised campaign is a broken link, not a global opt-out. Unsubscribing from
  // everything is what the RFC 8058 one-click endpoint does, and only a mail provider may call it —
  // reaching that outcome from a browser link whose campaign a mail client dropped is exactly what
  // the BFF's mandatory `category` rules out. Failing here keeps the 422 it would earn out of Sentry.
  if (!token || !category.success) {
    track('notification_unsubscribe_failed', { campaign: campaign ?? '' }, meta);
    return { status: 'invalid-link' };
  }

  try {
    await unsubscribeCommunicationPreferences({ body: { token, category: category.data }, throwOnError: true });
    track('notification_unsubscribe_completed', { campaign: campaign ?? '' }, meta);
    return { status: 'unsubscribed' };
  } catch (error) {
    track('notification_unsubscribe_failed', { campaign: campaign ?? '' }, meta);
    if (!(error instanceof ApiError) || error.status >= 500) {
      throw error;
    }
    // Return discarded: both known slugs render the same notice, an unknown one is what needs reporting.
    extractApiErrorCode(error, {
      flow: 'unsubscribe',
      byCode: new Set<UnsubscribeErrorCode>(['link-expired', 'link-invalid']),
    });
    return { status: 'invalid-link' };
  }
}
